import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
  completeLogin,
  getStoredUser,
  type RawTokens,
  startLogin,
  startLogout,
  storeTokens,
  userManager,
} from '@/auth/oidc.js';

/** Роль участия пользователя в проекте (приходит с бэка). */
export interface ProjectRole {
  projectId: string;
  role: 'enterprise' | 'region';
  regionCode?: string | null;
  enterpriseId?: string | null;
}

/**
 * Текущий пользователь promtourism-web.
 *
 * Идентичность приходит из JWT, но роли (`isAdmin`, участие в проектах) мы НЕ
 * читаем из токена — их выдаёт наш бэк через `GET /api/v1/me` на основании
 * локальных таблиц (`admins`, `enterprise_coordinators`, `project_participants`).
 */
export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  /** true, если sub есть в таблице `admins`. */
  isAdmin: boolean;
  /** UUID компаний, к которым прикреплён пользователь. */
  enterpriseIds: string[];
  /** Коды регионов, которые пользователь координирует. */
  regionCodes: string[];
  /** Участие в проектах. */
  participations: ProjectRole[];
}

interface MeResponse {
  id: string;
  email?: string;
  displayName?: string;
  isAdmin: boolean;
  enterpriseIds: string[];
  regionCodes: string[];
  participations: ProjectRole[];
  isServiceAccount: boolean;
}

const apiBase = import.meta.env.VITE_API_URL.replace(/\/$/, '');

async function fetchMe(token: string): Promise<MeResponse | null> {
  try {
    const r = await fetch(`${apiBase}/api/v1/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return null;
    return (await r.json()) as MeResponse;
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null);
  const user = ref<CurrentUser | null>(null);
  const isLoading = ref(true);

  const isAuthenticated = computed(() => !!user.value && !!accessToken.value);
  const isAdmin = computed(() => user.value?.isAdmin ?? false);
  const participations = computed<ProjectRole[]>(() => user.value?.participations ?? []);
  const isEnterpriseCoordinator = computed(() =>
    participations.value.some((p) => p.role === 'enterprise'),
  );
  const isRegionCoordinator = computed(() =>
    participations.value.some((p) => p.role === 'region'),
  );
  /** Есть ли у пользователя вообще доступ к внутренней части сервиса. */
  const hasAccess = computed(
    () => isAdmin.value || participations.value.length > 0,
  );

  async function hydrate(oidcUser: import('oidc-client-ts').User | null): Promise<void> {
    if (!oidcUser || oidcUser.expired) {
      accessToken.value = null;
      user.value = null;
      return;
    }
    accessToken.value = oidcUser.access_token;

    const me = await fetchMe(oidcUser.access_token);
    const profile = oidcUser.profile as Record<string, unknown>;
    const firstName = (profile.given_name as string | undefined) ?? '';
    const lastName = (profile.family_name as string | undefined) ?? '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    const profileEmail = (profile.email as string | undefined) ?? '';

    user.value = {
      id: oidcUser.profile.sub,
      email: me?.email ?? profileEmail,
      displayName:
        me?.displayName ||
        fullName ||
        (profile.preferred_username as string | undefined) ||
        profileEmail,
      isAdmin: me?.isAdmin ?? false,
      enterpriseIds: me?.enterpriseIds ?? [],
      regionCodes: me?.regionCodes ?? [],
      participations: me?.participations ?? [],
    };
  }

  async function initialize(): Promise<void> {
    isLoading.value = true;
    try {
      await hydrate(await getStoredUser());

      userManager.events.addUserLoaded((u) => {
        void hydrate(u);
      });
      userManager.events.addUserUnloaded(() => {
        void hydrate(null);
      });
      userManager.events.addAccessTokenExpired(() => {
        void hydrate(null);
      });
    } finally {
      isLoading.value = false;
    }
  }

  async function login(returnTo?: string, loginHint?: string): Promise<void> {
    await startLogin(returnTo, loginHint);
  }

  /** Авто-вход по токенам, полученным сразу после регистрации (без формы Keycloak). */
  async function loginWithTokens(tokens: RawTokens): Promise<void> {
    const oidcUser = await storeTokens(tokens);
    await hydrate(oidcUser);
  }

  async function handleCallback(): Promise<string> {
    const { user: oidcUser, returnTo } = await completeLogin();
    await hydrate(oidcUser);
    return returnTo;
  }

  async function logout(): Promise<void> {
    await startLogout();
  }

  /** Перетянуть `/me` с бэка — например, после присоединения к проекту. */
  async function refresh(): Promise<void> {
    if (!accessToken.value) return;
    const me = await fetchMe(accessToken.value);
    if (me && user.value) {
      user.value = {
        ...user.value,
        email: me.email ?? user.value.email,
        displayName: me.displayName ?? user.value.displayName,
        isAdmin: me.isAdmin,
        enterpriseIds: me.enterpriseIds,
        regionCodes: me.regionCodes,
        participations: me.participations,
      };
    }
  }

  return {
    accessToken,
    user,
    isAuthenticated,
    isAdmin,
    participations,
    isEnterpriseCoordinator,
    isRegionCoordinator,
    hasAccess,
    isLoading,
    initialize,
    login,
    loginWithTokens,
    handleCallback,
    logout,
    refresh,
  };
});
